import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import { metaRateFormSchema, type MetaRate, type MetaRateFormData } from "@/models/pricing";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { useUpsertMetaRate } from "../hooks/useUpsertMetaRate";

interface RateFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Pre-fills the form; saving with the same country and date replaces it. */
  rate?: MetaRate | null;
}

const today = () => new Date().toISOString().slice(0, 10);

const emptyForm = (): MetaRateFormData => ({
  country: "",
  effectiveFrom: today(),
  marketing: "",
  utility: "",
  authentication: "",
  service: "",
  label: ""
});

export function RateFormDialog({ open, onOpenChange, rate }: RateFormDialogProps) {
  const { mutate: upsertRate, isPending } = useUpsertMetaRate();
  const form = useForm<MetaRateFormData>({
    resolver: zodResolver(metaRateFormSchema),
    defaultValues: emptyForm()
  });

  useEffect(() => {
    if (!open) return;
    form.reset(
      rate
        ? {
            country: rate.country,
            effectiveFrom: rate.effectiveFrom.slice(0, 10),
            marketing: String(rate.marketing),
            utility: String(rate.utility),
            authentication: String(rate.authentication),
            service: rate.service !== undefined ? String(rate.service) : "",
            label: rate.label ?? ""
          }
        : emptyForm()
    );
  }, [open, rate]);

  const onSubmit = (data: MetaRateFormData) => {
    upsertRate(
      {
        country: data.country.toUpperCase(),
        effectiveFrom: new Date(`${data.effectiveFrom}T00:00:00.000Z`).toISOString(),
        marketing: Number(data.marketing),
        utility: Number(data.utility),
        authentication: Number(data.authentication),
        ...(data.service ? { service: Number(data.service) } : {}),
        label: data.label?.trim() || undefined
      },
      { onSuccess: () => onOpenChange(false) }
    );
  };

  const priceField = (
    name: "marketing" | "utility" | "authentication" | "service",
    label: string
  ) => (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{label}</FormLabel>
          <FormControl>
            <Input inputMode="decimal" placeholder="0.0000" {...field} />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{rate ? "Edit rate" : "Add rate"}</DialogTitle>
          <DialogDescription>
            USD per delivered message, as listed on Meta’s rate card for this market.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="country"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Country code</FormLabel>
                    <FormControl>
                      <Input placeholder="SA" maxLength={2} {...field} />
                    </FormControl>
                    <FormDescription>Use * for every country without its own row.</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="effectiveFrom"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Prices start on</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormDescription>Earlier days keep the previous rate.</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-3">
              {priceField("marketing", "Marketing")}
              {priceField("utility", "Utility")}
              {priceField("authentication", "Authentication")}
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {priceField("service", "Service (optional)")}
              <FormField
                control={form.control}
                name="label"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Market name (optional)</FormLabel>
                    <FormControl>
                      <Input placeholder="Saudi Arabia" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Leave service empty: Meta charges service messages at the market’s utility rate.
            </p>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Save rate
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
